import React, { useState } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { 
  FaBoxOpen, 
  FaBoxes, 
  FaPlus, 
  FaTags, 
  FaDollarSign, 
  FaEye,
  FaWarehouse
} from "react-icons/fa";
import { ToastContainer } from "react-toastify";
import StockInForm from "../inventory/StockInForm";
import StockOutForm from "../inventory/StockOutForm";
import AddNewItemForm from "../inventory/AddNewItemForm";
import AddBrandForm from "../inventory/AddBrandForm";
import PricingForm from "../inventory/PricingForm";
import InventoryDataViewer from "../inventory/InventoryDataViewer";
import "./InventorySubmenu.css";

const InventorySubmenu = () => {
  const navigate = useNavigate();
  // State for current view
  const [currentView, setCurrentView] = useState("menu"); // menu, stockIn, stockOut, newItem, brands, pricing, viewData
  const [selectedDataType, setSelectedDataType] = useState("");

  // Navigation functions
  const showStockInForm = () => setCurrentView("stockIn");
  const showStockOutForm = () => setCurrentView("stockOut");
  const showNewItemForm = () => setCurrentView("newItem");
  const showBrandsForm = () => setCurrentView("brands");
  const showPricingForm = () => setCurrentView("pricing");
  const showDataViewer = (dataType) => {
    setSelectedDataType(dataType);
    setCurrentView("viewData");
  };
  const backToMenu = () => setCurrentView("menu");

  // Inventory action buttons data
  const inventoryActions = [
    {
      id: 1,
      title: "Stock In",
      description: "Add stock to inventory",
      icon: <FaBoxOpen />,
      color: "success",
      action: showStockInForm,
      viewAction: () => showDataViewer("Stock In")
    },
    {
      id: 2,
      title: "Stock Out",
      description: "Remove stock from inventory",
      icon: <FaBoxes />,
      color: "warning",
      action: showStockOutForm,
      viewAction: () => showDataViewer("Stock Out")
    },
    {
      id: 3,
      title: "Add New Item",
      description: "Add new product to inventory",
      icon: <FaPlus />,
      color: "primary",
      action: showNewItemForm,
      viewAction: () => showDataViewer("Items")
    },
    {
      id: 4,
      title: "Add Brands",
      description: "Manage product brands",
      icon: <FaTags />,
      color: "info",
      action: showBrandsForm,
      viewAction: () => showDataViewer("Brands")
    },
    {
      id: 5,
      title: "Add/Modify Pricing",
      description: "Update product pricing",
      icon: <FaDollarSign />,
      color: "dark",
      action: showPricingForm,
      viewAction: () => showDataViewer("Pricing")
    },
    {
      id: 6,
      title: "Closing Stock",
      description: "View current stock levels",
      icon: <FaWarehouse />,
      color: "secondary",
      action: () => navigate('/inventory/closing-stock'),
      viewAction: () => navigate('/inventory/closing-stock')
    }
  ];

  // Render current view
  const renderCurrentView = () => {
    switch (currentView) {
      case "stockIn":
        return <StockInForm onBack={backToMenu} />;
      case "stockOut":
        return <StockOutForm onBack={backToMenu} />;
      case "newItem":
        return <AddNewItemForm onBack={backToMenu} />;
      case "brands":
        return <AddBrandForm onBack={backToMenu} />;
      case "pricing":
        return <PricingForm onBack={backToMenu} />;
      case "viewData":
        return <InventoryDataViewer dataType={selectedDataType} onBack={backToMenu} />;
      default:
        return (
          <div className="inventory-submenu-container">
            <div className="text-center mb-4">
              <h2 className="text-primary">
                <FaBoxOpen className="me-2" />
                Inventory Management
              </h2>
              <p className="text-muted">Manage your restaurant inventory efficiently</p>
            </div>
            
            <Row className="g-4">
              {inventoryActions.map((action) => (
                <Col key={action.id} lg={4} md={6} sm={12}>
                  <Card className="h-100 shadow-sm inventory-action-card border-0">
                    <Card.Body className="text-center p-4">
                      <div className={`action-icon-container bg-${action.color} text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center`} style={{width: '60px', height: '60px', fontSize: '24px'}}>
                        {action.icon}
                      </div>
                      <h5 className="action-title mb-2">{action.title}</h5>
                      <p className="action-description text-muted mb-4">{action.description}</p>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant={action.color}
                          size="md"
                          className="flex-fill"
                          onClick={action.action}
                        >
                          <FaPlus className="me-1" />
                          Add
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="md"
                          className="flex-fill"
                          onClick={action.viewAction}
                        >
                          <FaEye className="me-1" />
                          View
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        );
    }
  };

  return (
    <>
      <ToastContainer />
      {renderCurrentView()}
    </>
  );
};

export default InventorySubmenu;